import type { ReactNode } from 'react';

import { usePathname } from '@classroom-ai/ui/routes/hooks';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { useActiveClass, defaultSchedulePeriod } from 'src/class/class-provider';

import { AskAiDrawer } from './ask-ai-drawer';
import { type TargetLike, isBeaconShortcut } from './beacon-shortcut';
import { viewContextFor, type AskAiViewContext } from './ask-ai-utils';

// ----------------------------------------------------------------------

type AskAiContextValue = {
  /**
   * Open the drawer for the active class. `view` overrides the page context, e.g. the
   * weekly grid (#418) opens it about one lesson while the teacher stays on the dashboard.
   * Callers that target another class set the active class first. `question` is asked
   * right away (e.g. the score explainer's "Ask AI to explain", epic #547).
   */
  openAskAi: (view?: AskAiViewContext, question?: string) => void;
  /** A page's own, richer context (e.g. the Standards scope toggle); null clears it. */
  setPageView: (page: AskAiPage | null) => void;
};

/**
 * Which class Ask AI answers about, when the page's course is not the active class's —
 * e.g. a Department Head on the Lesson Plans board of a course they don't teach (any
 * section stands for the course plan; the agent checks school-year staff access).
 */
export type AskAiTarget = { classId: string; classLabel: string };

type AskAiPage = { view: AskAiViewContext; target?: AskAiTarget | null };

const AskAiContext = createContext<AskAiContextValue | null>(null);

/**
 * Owns the one Ask AI drawer (epic #391) so both the header button and in-page entry
 * points open the same drawer and transcript.
 */
export function AskAiProvider({ children }: { children: ReactNode }) {
  const { activeClass } = useActiveClass();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [viewOverride, setViewOverride] = useState<AskAiViewContext | null>(null);
  const [registered, setPageView] = useState<AskAiPage | null>(null);
  const pathView = useMemo(() => viewContextFor(pathname), [pathname]);
  // A registering page clears it on unmount, so it never outlives the page.
  const pageView = registered?.view ?? pathView;
  const target = registered?.target ?? null;

  const [question, setQuestion] = useState<{ text: string; key: number } | null>(null);
  const [focusKey, setFocusKey] = useState(0);

  const openAskAi = useCallback((view?: AskAiViewContext, text?: string) => {
    setViewOverride(view ?? null);
    if (text) setQuestion({ text, key: Date.now() });
    setFocusKey((k) => k + 1);
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ openAskAi, setPageView }), [openAskAi]);

  // `/` or ⌘K / Ctrl+K opens Beacon from any page (epic #617), whenever there is a class to
  // ask about — the same rule as the header button.
  const hasClass = !!(registered?.target?.classId ?? activeClass?.id);
  useEffect(() => {
    if (!hasClass) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!isBeaconShortcut(e, e.target as TargetLike)) return;
      e.preventDefault();
      openAskAi();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasClass, openAskAi]);

  const period = activeClass ? defaultSchedulePeriod(activeClass)?.name : undefined;
  const classId = target?.classId ?? activeClass?.id ?? null;
  const classLabel = target
    ? target.classLabel
    : activeClass
      ? [activeClass.course.name, period].filter(Boolean).join(' · ')
      : '';

  return (
    <AskAiContext.Provider value={value}>
      {children}
      {classId && (
        <AskAiDrawer
          open={open}
          onClose={() => setOpen(false)}
          classId={classId}
          classLabel={classLabel}
          view={viewOverride ?? pageView}
          initialQuestion={question}
          focusKey={focusKey}
        />
      )}
    </AskAiContext.Provider>
  );
}

/**
 * Register the current page's Ask AI context for as long as the page shows it; `target`
 * points the drawer at another class (see `AskAiTarget`).
 */
export function useAskAiPageView(view: AskAiViewContext | null, target?: AskAiTarget | null) {
  const { setPageView } = useAskAi();
  const key = view ? JSON.stringify({ view, target: target ?? null }) : '';
  useEffect(() => {
    setPageView(key ? (JSON.parse(key) as AskAiPage) : null);
    return () => setPageView(null);
  }, [key, setPageView]);
}

export function useAskAi(): AskAiContextValue {
  const ctx = useContext(AskAiContext);
  if (!ctx) throw new Error('useAskAi must be used inside AskAiProvider');
  return ctx;
}
